import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, ChevronDown, ChevronRight,
  Lock, PlayCircle, CheckCircle2, Download, Sparkles, Headphones,
} from "lucide-react";
import { readerDownloadUrl } from "../lib/api";

// Slugs that currently have a "Meet {person}" audio drama available.
// Kept in sync with /app/backend/routers/portraits.py FIGURE_NAMES.
const PORTRAIT_AUDIO_SLUGS = new Set([
  "ada_lovelace", "grace_hopper", "florence_nightingale", "mary_jackson",
  "maria_sibylla_merian", "sophie_germain", "timnit_gebru", "cynthia_rudin",
  "katherine_johnson", "eliza",
  "alan_turing", "m_c_escher", "donald_knuth", "tim_berners_lee",
  "alexander_graham_bell", "john_tukey", "thomas_bayes", "frank_rosenblatt",
  "claude_shannon", "norbert_wiener",
]);

/**
 * ChapterCard — one of five chapters in a term.
 *
 * Props:
 *   chapter        object from GET /api/readers/{band}/term_{n}
 *   ageBand        e.g. "age_8-9"
 *   termInt        1 | 2 | 3
 *   lessons        the 4 lesson docs behind this chapter (from catalog)
 *   lessonState    (lessonId) => { done: bool, unlocked: bool }
 *   accent         color hex for the band
 *   onUpgradeClick callback for the Upgrade CTA when chapter is locked
 */
export default function ChapterCard({
  chapter, ageBand, termInt, lessons, lessonState, accent, onUpgradeClick,
}) {
  const [open, setOpen] = useState(chapter.chapter_num === 1);
  const {
    chapter_num, title, subtitle, person_name, portrait_slug,
    lesson_count, completed_lessons, is_accessible, pdf_available,
  } = chapter;

  const progressPct = lesson_count ? (completed_lessons / lesson_count) * 100 : 0;
  const meetAudioAvailable = portrait_slug && PORTRAIT_AUDIO_SLUGS.has(portrait_slug);

  return (
    <section
      className={`ftg-chapter${is_accessible ? "" : " is-locked"}`}
      data-testid={`chapter-card-${chapter_num}`}
    >
      <header className="ftg-chapter__head">
        <button
          type="button"
          className="ftg-chapter__toggle"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          data-testid={`chapter-toggle-${chapter_num}`}
        >
          <span
            className="ftg-chapter__num"
            style={{ color: accent, borderColor: `${accent}40` }}
            aria-hidden="true"
          >
            {String(chapter_num).padStart(2, "0")}
          </span>
          <span className="ftg-chapter__titles">
            <span className="ftg-chapter__title">{title}</span>
            {subtitle ? (
              <span className="ftg-chapter__sub">{subtitle}</span>
            ) : null}
          </span>
          <span className="ftg-chapter__chev" aria-hidden="true">
            {open ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </span>
        </button>
        {/* "Meet {person}" chip + optional "Listen" pill live OUTSIDE the
            toggle <button> so we can keep the HTML valid (no <a> in <button>)
            and give both a proper inline row. Aligned under the title
            column via padding-left. */}
        {person_name ? (
          <div className="ftg-chapter__meet-row" aria-hidden={false}>
            <span className="ftg-chapter__person">
              <Sparkles size={12} /> Meet {person_name}
            </span>
            {meetAudioAvailable ? (
              <Link
                to={`/meet/${portrait_slug}`}
                className="ftg-meet-btn"
                data-testid={`chapter-meet-btn-${chapter_num}`}
              >
                <Headphones size={12} /> Listen (2 min)
              </Link>
            ) : null}
          </div>
        ) : null}

        <div className="ftg-chapter__aside">
          <div className="ftg-chapter__progress" aria-hidden="true">
            <div className="ftg-chapter__progress__label">
              {completed_lessons}/{lesson_count} lessons
            </div>
            <div className="ftg-chapter__progress__bar">
              <span
                className="ftg-chapter__progress__fill"
                style={{ width: `${progressPct}%`, background: accent }}
              />
            </div>
          </div>

          {is_accessible && pdf_available ? (
            <a
              href={readerDownloadUrl(ageBand, termInt, chapter_num)}
              target="_blank"
              rel="noopener noreferrer"
              className="ftg-btn ftg-btn--primary ftg-chapter__reader-btn"
              data-testid={`chapter-reader-btn-${chapter_num}`}
            >
              <BookOpen size={16} /> Download Reader
            </a>
          ) : is_accessible ? (
            <span
              className="ftg-chapter__reader-btn ftg-chapter__reader-btn--missing"
              data-testid={`chapter-reader-missing-${chapter_num}`}
            >
              Reader coming soon
            </span>
          ) : (
            <button
              type="button"
              className="ftg-btn ftg-btn--ghost ftg-chapter__reader-btn"
              onClick={onUpgradeClick}
              data-testid={`chapter-upgrade-${chapter_num}`}
            >
              <Lock size={14} /> Upgrade to unlock
            </button>
          )}
        </div>
      </header>

      {open ? (
        <div className="ftg-chapter__body" data-testid={`chapter-body-${chapter_num}`}>
          {lessons.length === 0 ? (
            <div className="ftg-chapter__body__empty">Lessons loading…</div>
          ) : (
            lessons.map((l) => {
              const st = lessonState(l.id);
              return (
                <div
                  key={l.id}
                  className={`ftg-chapter__lesson${st.unlocked ? "" : " is-locked"}${st.done ? " is-done" : ""}`}
                  data-testid={`chapter-lesson-${l.id}`}
                >
                  <div className="ftg-chapter__lesson__meta">
                    <span className="ftg-chapter__lesson__pos">
                      Week {String(l.week).padStart(2, "0")} · Part {l.lesson_number}
                    </span>
                    <span className="ftg-chapter__lesson__title">{l.title}</span>
                  </div>
                  <div className="ftg-chapter__lesson__cta">
                    {st.done ? (
                      <span className="ftg-chapter__lesson__done" style={{ color: accent }}>
                        <CheckCircle2 size={16} /> Completed
                      </span>
                    ) : st.unlocked ? (
                      <Link
                        to={`/lesson/${encodeURIComponent(l.id)}`}
                        className="ftg-btn ftg-btn--ghost"
                        data-testid={`chapter-open-lesson-${l.id}`}
                      >
                        <PlayCircle size={14} /> Open
                      </Link>
                    ) : (
                      <span className="ftg-chapter__lesson__lock">
                        <Lock size={12} /> Locked
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : null}
    </section>
  );
}
